import axios from 'axios';
import { parseUrl, stringifyUrl } from 'query-string';
import { SimpleEvent } from '@eastsideco/salvo-ts';

export interface CollectionState {
    filters: Record<string, string[]>;
    sortBy: string | null;
    page: number | undefined;
}

const DEFAULT_STATE: CollectionState = {
    filters: {},
    sortBy: null,
    page: undefined,
};

export class CollectionService {

    private _state: CollectionState;
    public get state(): CollectionState {
        return this._state;
    }

    private _onChange = new SimpleEvent<CollectionState>();
    public get onChange() {
        return this._onChange.asEvent();
    }

    public constructor() {
        this._state = this.parseState();
    }

    public parseState(fromUrl?: string): CollectionState {

        // Define the default state
        const state: CollectionState = DEFAULT_STATE;

        // If no URL has been provided, use the current URL
        if (!fromUrl) {
            if (!window.location?.href) { // Workaround for component metadata generation
                return state;
            }
            fromUrl = window.location.href;
        }

        // Update and return state params
        const { query } = parseUrl(fromUrl);
        state.page = query.page ? Number(query.page) : undefined;
        state.sortBy = query.sort_by?.toString() || null;
        state.filters = {};
        for (const k of Object.keys(query)) {
            if (k.indexOf('filter.') == 0) {
                const value = query[k];
                if (!value) {
                    continue;
                }
                if (typeof value == 'string') {
                    state.filters[k] = [value];
                } else {
                    state.filters[k] = value;
                }
            }
        }
        return state;
    }

    public async render(sections: string[] = []): Promise<Record<string, string>> {

        // Build URL
        const { url, query } = parseUrl(window.location.href, { decode: false });
        const newUrl = stringifyUrl({
            url: url,
            query: {
                ...query,
                sections: sections.map((s) => encodeURIComponent(s)).join(','),
            }
        }, { arrayFormat: 'none', encode: false });

        // Get and return data
        const res = await axios.get(newUrl);
        return res.data;
    }

    public async update(newState: CollectionState) {

        // Build query string
        const query: Record<string, string | string[] | undefined> = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            sort_by: newState.sortBy || undefined,
            page: newState.page?.toString() || undefined,
        };
        for (const key of Object.keys(newState.filters)) {
            query[key] = newState.filters[key].map((s) => encodeURIComponent(s).replace(/%20/g, '+'));
        }

        // Update URL
        const { url } = parseUrl(window.location.href);
        const newUrl = stringifyUrl({ url, query }, { arrayFormat: 'none', encode: false });
        window.history.replaceState({}, document.title, newUrl);

        // Update state
        this._state = newState;

        // Dispatch event
        this._onChange.dispatch(newState);
    }
}

export const collection = new CollectionService();
